import { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Heading,
  Container,
  Stack,
  StackSeparator,
  Box,
  Flex,
  Text,
  Badge,
  Button,
  Spacer,
  Icon,
  Spinner,
} from '@chakra-ui/react';
import { useNavigate, useParams, NavLink } from 'react-router-dom';
import { jwtDecode } from 'jwt-decode';
import { PlusIcon } from '@heroicons/react/24/outline';
import { toaster } from '@/components/ui/toaster';
import CreateAccountModal from './create-account-modal';
import { useAuth } from '@/lib/hooks/use-auth';
import useModalToggle from '@/lib/hooks/use-modal-toggle';
import useWorkspaceStore from '@/utils/use-workspace';
import { AdminRoles, ExtendedWorkspace, SportType } from '@/lib/types';
import api from '@/utils/api';
import { totalDays, orderWorkspaces } from '@/lib/helpers';

interface DecodedToken {
  role_id: number;
}

export default function ListWorkspace() {
  const { workspaces } = useWorkspaceStore();
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const { workspaceId } = useParams();
  const { changeWorkspace, changeRole } = useAuth();
  const [sportTypes, setSportTypes] = useState<Map<number, string>>(new Map());
  const createModal = useModalToggle();

  const items = workspaceId ? orderWorkspaces(workspaces, workspaceId) : [];

  const handleWorkspace = async (workspace: ExtendedWorkspace) => {
    try {
      if (checkCurrentWorkspace(workspace.id)) return;

      const response = await api.get(`workspace/${workspace.id}/token`);
      if (response.data.resource) {
        const decoded = jwtDecode<DecodedToken>(response.data.resource);
        localStorage.setItem('accessToken', response.data.resource);
        changeWorkspace(workspace);
        changeRole({
          id: decoded?.role_id,
          name: workspace.role_name,
        });
        navigate(`/${workspace.id}/home`);
      }
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to change workspace',
      });
    }
  };

  function checkCurrentWorkspace(id: number) {
    return workspaceId && parseFloat(workspaceId) === id;
  }

  const fetchData = useCallback(async () => {
    try {
      setIsLoading(true);

      const response = await api.get(`/public/sport-types`);
      const map = new Map<number, string>();

      response.data.resource.forEach((item: SportType) => {
        map.set(item.id, item.name);
      });

      setSportTypes(map);
    } catch (err: any) {
      toaster.create({
        type: 'error',
        title: err.response?.data?.error || 'Failed to fetch content',
      });
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function isCancelled(item: ExtendedWorkspace) {
    return item?.plan_end_date && totalDays(item.plan_end_date) < 0;
  }

  return (
    <Card.Root minH={'100vh'} mx={'auto'} width={{ md: '90%' }} maxW={'6xl'}>
      <Card.Header>
        <Container maxW={'3xl'}>
          <Flex mb={4}>
            <Heading size={'3xl'} mb={4}>
              Workspaces
            </Heading>
            <Spacer />
            <Button colorPalette='brand' onClick={createModal.open}>
              <Icon as={PlusIcon} w={4} h={4} />
              Create
            </Button>
          </Flex>
        </Container>
      </Card.Header>
      <Card.Body>
        <Container maxW={'3xl'} mt={6}>
          {isLoading ? (
            <Flex justifyContent={'center'} alignItems={'center'} height={'100%'}>
              <Spinner size={'xl'} />
            </Flex>
          ) : (
            <Stack gap={4} separator={<StackSeparator />}>
              {items.map((row) => (
                <Box
                  key={row.id}
                  _hover={{ cursor: 'pointer' }}
                  onClick={() => handleWorkspace(row)}
                >
                  <Flex w={'full'} alignItems='center' flexWrap='wrap'>
                    <Text fontSize={'sm'} fontWeight={'medium'} _hover={{ color: 'brand' }}>
                      {row.name}
                      {row.sport_type_id && sportTypes.has(row.sport_type_id) && (
                        <Badge variant='surface' ml={2}>
                          {sportTypes.get(row.sport_type_id)}
                        </Badge>
                      )}
                    </Text>
                    <Spacer />
                    {row.role_id === AdminRoles.WorkspaceAdmin &&
                      !isCancelled(row) &&
                      checkCurrentWorkspace(row.id) && (
                        <NavLink to={`/${row.id}/account/edit`}>
                          <Button colorPalette='brand' size={'xs'} zIndex={99}>
                            Edit
                          </Button>
                        </NavLink>
                      )}
                  </Flex>
                </Box>
              ))}
            </Stack>
          )}
        </Container>
      </Card.Body>

      <CreateAccountModal {...createModal.props} onSuccess={() => navigate(0)} />
    </Card.Root>
  );
}
